import StudentIdCard, { StudentCardDataArgs } from '@Components/Cards/StudentIdCard'
import EdTypography from '@Components/Utilities/EdTypography/EdTypography'
import { useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { Dialog, RaisedButton } from '@eduact/ed-components'
import Spacer from '@eduact/ed-components/Spacer'
import { Box, FlexLayout } from '@eduact/ed-system'
import html2canvas from 'html2canvas'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const ProfileStudentCard = () => {
	const [t] = useTranslation('studentDashboard')
	const [tEdu] = useTranslation('educational_info')
	const [viewOpen, setViewOpen] = useState(false)

	const { data } = useGetUserProfileQuery()
	const profile = data?.data
	const student = profile?.student

	const downloadRef = useRef<HTMLDivElement | null>(null)

	// Build the shared card's data from the dashboard profile query. Educational
	// names are resolved through the same `educational_info` namespace the old
	// card used; the QR encodes the student username.
	const cardData = useMemo<StudentCardDataArgs>(() => {
		return {
			userInfo: {
				phone: profile?.phone_number,
				first_name: profile?.first_name,
				last_name: profile?.last_name,
				username: profile?.username,
				profile_photo: profile?.profile_photo,
			},
			educationalInfo: {
				diploma: student?.educationType?.name ? tEdu(`educational_type.${student.educationType.name}`) : '',
				language: student?.educationLanguage?.name ? tEdu(`academic_lang.${student.educationLanguage.name}`) : '',
				year: student?.educationYear?.name ? tEdu(`academic_year.${student.educationYear.name}`) : '',
				section: student?.educationSection?.name ? tEdu(`educational_field.${student.educationSection.name}`) : '',
			},
		}
	}, [profile, student, tEdu])

	const handleCardDownload = useCallback(() => {
		if (!downloadRef.current) return
		html2canvas(downloadRef.current).then(canvas => {
			const downloadLink = document.createElement('a')
			downloadLink.href = canvas.toDataURL('image/jpeg')
			downloadLink.download = 'Student_Card.png'
			downloadLink.click()
		})
	}, [])

	return (
		<Box>
			<Dialog
				width={{ lg: 'unset' }}
				p={{ lg: '2rem' }}
				minWidth={{ sm: '90vw', lg: '40vw' }}
				minHeight={{ sm: 'unset', lg: 'fit-content' }}
				height={{ sm: 'fit-content', lg: 'fit-content' }}
				open={viewOpen}
				onClose={() => setViewOpen(false)}
			>
				<FlexLayout height={'100%'} width={'100%'} alignItems={'center'} justifyContent={'center'} flexDirection="column" gridGap={'2rem'}>
					<EnlargedCard>
						<StudentIdCard data={cardData} />
					</EnlargedCard>
					<RaisedButton onClick={() => setViewOpen(false)} btnSize="small" variant="princetonOrange">
						Close
					</RaisedButton>
				</FlexLayout>
			</Dialog>

			<DownloadArea ref={downloadRef}>
				<StudentIdCard data={cardData} />
			</DownloadArea>

			<Spacer mb={{ lg: 3 }} />
			<FlexLayout gridGap={{ sm: '4rem', lg: '2rem' }} mt={{ sm: '2rem', lg: 'unset' }}>
				<CardAction onClick={() => setViewOpen(true)} color="primary">
					{t('view')}
				</CardAction>
				<CardAction onClick={handleCardDownload} color="purple">
					{t('download')}
				</CardAction>
			</FlexLayout>
		</Box>
	)
}

export default ProfileStudentCard

const CardAction = styled(EdTypography)`
	text-decoration: underline;
	font-weight: 600;
	cursor: pointer;
`

const DownloadArea = styled.div`
	max-width: 400px;
`

const EnlargedCard = styled.div`
	width: min(92vw, 460px);
`
